import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SITE, { } from '../api';
import { useSite, IMG } from '../components/StoreLayout';
import { usePortal, PORTAL } from '../portalAuth';
import useSEO from '../useSEO';
import { STORE_BASE } from '../routeBase';

// Icon per trust-strip slot — fixed in code (design decision), text comes
// from site_content (strip.1..strip.7), editable in Website Editor.
const TRUST_STRIP = [
  { key: 'strip.1', icon: 'ti-world' },              // Exporting to 40+ Countries
  { key: 'strip.2', icon: 'ti-building-factory-2' },  // OEM & Private Label
  { key: 'strip.3', icon: 'ti-eyeglass-2' },          // 5,000+ Premium Designs
  { key: 'strip.4', icon: 'ti-package' },             // Ready Stock Inventory
  { key: 'strip.5', icon: 'ti-truck-delivery' },      // Fast Worldwide Shipping
  { key: 'strip.6', icon: 'ti-currency-dollar' },     // Factory Pricing
  { key: 'strip.7', icon: 'ti-heart-handshake' },     // Low MOQ
];

export default function Home() {
  const { content: c, categories } = useSite();
  let portal = null;
  try { portal = usePortal(); } catch (e) { portal = null; }
  const customer = portal?.customer;
  const [women, setWomen] = useState([]);
  const [men, setMen] = useState([]);
  const [kids, setKids] = useState([]);

  useSEO({
    title: c['homepage.meta_title'] || c['seo.title'] || 'Wholesale Eyeglass Frames & Sunglasses Manufacturer',
    description: c['homepage.meta_description'] || c['seo.description'] || `${c['brand.name'] || 'Salvador Eyewear'} manufactures and exports wholesale acetate, TR90 and metal eyeglass frames and sunglasses to distributors and retailers worldwide.`,
    keywords: c['homepage.meta_keywords'] || 'wholesale eyeglass frames, eyewear manufacturer, acetate frames, TR90 frames, metal frames, sunglasses supplier, optical frames India',
    canonical: `${window.location.origin}/store`,
  });

  useEffect(() => {
    // Logged-in customers get their own priced listing from the portal API
    // (adds r.price per model) — guests get the public, price-less listing.
    const client = customer ? PORTAL : SITE;
    client.get('/products', { params: { gender: 'Women', limit: 4 } }).then(r => setWomen(r.data.products || []));
    client.get('/products', { params: { gender: 'Men', limit: 4 } }).then(r => setMen(r.data.products || []));
    client.get('/products', { params: { gender: 'Kids', limit: 4 } }).then(r => setKids(r.data.products || []));
  }, [customer]);

  // Hexashop-style promo banner: a large hero panel (image uploaded later
  // by the admin) beside a 2x2 grid of Women/Men/Kids/Accessories. Women,
  // Men and Kids reuse the site's existing category photos; the hero and
  // Accessories images start blank until uploaded via the Website Editor —
  // both slots render a plain dark/neutral placeholder rather than a broken
  // image, and the whole section is skipped only if every one of the 6
  // underlying images is unset.
  const heroBanner = c['homepage.hero_banner'];
  const heroTitle = c['homepage.hero_title'];
  const heroTagline = c['homepage.hero_tagline'];
  const heroCtaLabel = c['homepage.hero_cta_label'];
  const heroCtaUrl = c['homepage.hero_cta_url'] || `${STORE_BASE}/shop?new=1`;
  const bannerWomen = c['homepage.home_banner_4'];
  const bannerMen = c['homepage.home_banner_1'];
  const bannerKids = c['homepage.home_banner_2'];
  const bannerAccessories = c['homepage.hero_accessories'];
  const showPromoGrid = [heroBanner, bannerWomen, bannerMen, bannerKids, bannerAccessories].some(Boolean);

  // Three unlabeled rectangle boxes between Men's Collection and Shop by
  // Category — reuse home_banner_5/7/8 (already uploaded in the Website
  // Editor) as plain image links to their own content page, no title/caption
  // overlay since the banner artwork already carries its own message.
  const bannerLenses = c['homepage.home_banner_5'];
  const bannerGoggles = c['homepage.home_banner_7'];
  const bannerGold = c['homepage.home_banner_8'];
  const showServiceStrip = [bannerLenses, bannerGoggles, bannerGold].some(Boolean);

  return (
    <div>
      {/* promo banner grid — Hexashop layout. Proportions are set via CSS
          classes (not inline styles) so the mobile media query below can
          actually override them — an inline aspect-ratio would otherwise
          squash every card into an unreadable sliver on narrow screens. */}
      {showPromoGrid && (
        <section className="sf-fade-in" style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 24px 4px' }}>
          <style>{`
            /* 9fr/8fr + 17/8 aren't arbitrary — they're the exact split that
               makes the hero panel render at 900x800 (its documented upload
               size) and each of the 4 side cells render at a true 500x500
               square (their documented size) at the same time. The two were
               previously locked to the same ratio by equal 1fr/1fr columns
               (a plain 1fr split makes the hero and a side cell always end
               up the same shape), so a 500x500 upload was silently getting
               cropped in the cells. Change these two numbers together, not
               independently, if this layout is ever revisited — see the
               matching image-size hints in WebsiteEditor.js's IMAGE_HINTS. */
            .sf-promo-grid { display:grid; grid-template-columns:9fr 8fr; gap:16px; aspect-ratio:17/8; }
            .sf-promo-side { display:grid; grid-template-columns:1fr 1fr; grid-template-rows:1fr 1fr; gap:16px; }
            @media (max-width:700px) {
              .sf-promo-grid { grid-template-columns:1fr; aspect-ratio:auto; }
              .sf-hero-panel { aspect-ratio:9/8; }
              .sf-promo-side { aspect-ratio:1/1; }
            }
          `}</style>
          <div className="sf-promo-grid">
            <HeroPanel src={heroBanner} title={heroTitle} tagline={heroTagline} ctaLabel={heroCtaLabel} ctaUrl={heroCtaUrl} />
            <div className="sf-promo-side">
              <CategoryCard src={bannerWomen} title={c['homepage.card_women_title'] || 'Women'} subtitle={c['homepage.card_women_subtitle'] || 'Best Frames For Women'} to={`${STORE_BASE}/shop?gender=Women`} />
              <CategoryCard src={bannerMen} title={c['homepage.card_men_title'] || 'Men'} subtitle={c['homepage.card_men_subtitle'] || 'Best Frames For Men'} to={`${STORE_BASE}/shop?gender=Men`} />
              <CategoryCard src={bannerKids} title={c['homepage.card_kids_title'] || 'Kids'} subtitle={c['homepage.card_kids_subtitle'] || 'Best Frames For Kids'} to={`${STORE_BASE}/shop?gender=Kids`} />
              <CategoryCard src={bannerAccessories} title={c['homepage.card_accessories_title'] || 'Accessories'} subtitle={c['homepage.card_accessories_subtitle'] || 'Best Trend Accessories'} to={`${STORE_BASE}/shop?category=Eyewear%20Accessories`} />
            </div>
          </div>
        </section>
      )}

      {/* trust strip — 7 quick USPs. The icon per slot is fixed in code (kept
          in sync with the theme accent colour); only the text is
          admin-editable (Website Editor > Content & Images > Home Page,
          "Trust strip 1 of 7" etc). Grid columns collapse 7 → 4 → 2 as the
          viewport narrows, so it never gets cramped on tablet/mobile. */}
      {TRUST_STRIP.some(({ key }) => c[key]) && (
        <div style={{ background: '#fff', borderTop: '1px solid #eee', borderBottom: '1px solid #eee', marginTop: 36 }}>
          <style>{`
            .sf-trust-strip { display: grid; grid-template-columns: repeat(7, 1fr); gap: 10px; }
            @media (max-width: 900px) { .sf-trust-strip { grid-template-columns: repeat(4, 1fr); row-gap: 26px; } }
            @media (max-width: 480px) { .sf-trust-strip { grid-template-columns: repeat(2, 1fr); } }
          `}</style>
          <div className="sf-trust-strip" style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 20px' }}>
            {TRUST_STRIP.map(({ key, icon }) => c[key] && (
              <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 9, padding: '0 4px' }}>
                <i className={`ti ${icon}`} style={{ fontSize: 30, color: 'var(--sf-accent)' }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: '#222', lineHeight: 1.35 }}>{c[key]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* register CTA — replaces the old "Shop by Gender and Frame Type"
          icon row on the website (that shortcut stays mobile-app-only, per
          request — not rendered here). Light card style with an outline
          button, using the site-wide hover system (.sf-btn-outline).
          A logged-in customer already has wholesale pricing, so "Register
          now" reads as broken rather than just irrelevant — swap it for a
          retention CTA into their own Dashboard instead of hiding the row
          (same card slot, no layout jump, still earns its place on the page). */}
      <section style={{ maxWidth: 1200, margin: '30px auto', padding: '0 24px' }}>
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', boxShadow: '0 4px 18px rgba(0,0,0,0.07)', borderRadius: 14, padding: '26px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 18 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#191919', margin: 0 }}>
            {customer
              ? `Welcome back, ${customer.company} — track your orders and reorder in one click.`
              : (c['register.cta'] || 'Register now to unlock wholesale pricing and start ordering today.')}
          </h2>
          <Link to={customer ? `${STORE_BASE}/dashboard` : `${STORE_BASE}/register`} className="sf-btn-outline" style={{ border: '1.5px solid var(--sf-accent)', padding: '11px 30px', borderRadius: 24, fontSize: 14, fontWeight: 600, textDecoration: 'none', flexShrink: 0 }}>
            {customer ? 'Go to Dashboard' : 'Register Now'}
          </Link>
        </div>
      </section>

      <Collection title="Women's Collection" gender="Women" products={women} />
      <Collection title="Men's Collection" gender="Men" products={men} />

      {/* service strip — 3 plain image links (no title) to Stock & Rx Lenses,
          Rx Swimming Goggles and 18 Carat Pure Gold Frames. Each is its own
          content page, not a filtered product grid — these aren't catalog
          SKUs today. Generous top/bottom margin (36px, not the old 8px) so
          it reads as its own row between Men's Collection and Kid's
          Collection instead of crowding into both. */}
      {showServiceStrip && (
        <section style={{ maxWidth: 1600, margin: '36px auto', padding: '0 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
            <PromoBox src={bannerLenses} to={`${STORE_BASE}/page/stock-rx-lenses`} />
            <PromoBox src={bannerGoggles} to={`${STORE_BASE}/page/rx-swimming-goggles`} />
            <PromoBox src={bannerGold} to={`${STORE_BASE}/page/pure-gold-frames`} />
          </div>
        </section>
      )}

      <Collection title="Kid's Collection" gender="Kids" products={kids} />

      {/* category grid — only categories with a real curated photo show here;
          everything still browsable, just via the full Shop filter list.
          Fixed 3-per-row, same 1600 width and 7/4 aspect-ratio tile as the
          service strip above, so both rows read as one consistent size —
          no more user-adjustable column count/text size controls. */}
      <section style={{ background: '#fff', padding: '44px 0 34px' }}>
        <div style={{ maxWidth: 1600, margin: '0 auto', padding: '0 24px' }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 16px' }}>Shop by Category</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
            {categories.filter(cat => cat.image).map((cat) => (
              <Link key={cat.name} to={`${STORE_BASE}/shop?category=${encodeURIComponent(cat.name)}`}
                style={{ textDecoration: 'none', color: 'inherit' }}>
                <div style={{ aspectRatio: '7 / 4', borderRadius: 14, overflow: 'hidden', position: 'relative', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
                  <img src={IMG(cat.image)} alt={cat.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 55%)' }} />
                  <div style={{ position: 'absolute', left: 14, right: 14, bottom: 12, color: '#fff' }}>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>{cat.name.replace('Salvador Eyewear - ', '')}</div>
                    <div style={{ fontSize: 12, opacity: 0.85 }}>{cat.count} items</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function HeroPanel({ src, title, tagline, ctaLabel, ctaUrl }) {
  // The whole panel (title + tagline + button) is one click target to
  // New Arrivals — the button is a styled <span>, not a nested <Link>,
  // since an <a> can't legally contain another <a>.
  return (
    <Link to={ctaUrl} className="sf-hero-panel" style={{ position: 'relative', display: 'block', borderRadius: 10, overflow: 'hidden', background: '#232323', textDecoration: 'none' }}>
      {src && <img src={IMG(src)} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.5) 100%)' }} />
      <div style={{ position: 'absolute', left: 28, right: 28, bottom: 32, color: '#fff' }}>
        {title && <h2 style={{ fontSize: 'clamp(20px, 2.6vw, 32px)', fontWeight: 700, margin: '0 0 8px', lineHeight: 1.2 }}>{title}</h2>}
        {tagline && <div style={{ fontStyle: 'italic', fontSize: 14.5, opacity: 0.92, margin: '0 0 18px' }}>{tagline}</div>}
        {ctaLabel && (
          <span style={{ display: 'inline-block', border: '1.5px solid #fff', color: '#fff', padding: '9px 24px', borderRadius: 2, fontSize: 12.5, fontWeight: 600, letterSpacing: 0.5 }}>
            {ctaLabel}
          </span>
        )}
      </div>
    </Link>
  );
}

function CategoryCard({ src, title, subtitle, to }) {
  return (
    <Link to={to} style={{ position: 'relative', display: 'block', height: '100%', borderRadius: 10, overflow: 'hidden', background: '#e8e8e8', textDecoration: 'none' }}>
      {src && <img src={IMG(src)} alt={title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
      {!src && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 12.5 }}>Image coming soon</div>}
      {src && <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 55%)' }} />}
      <div style={{ position: 'absolute', left: 16, right: 16, bottom: 14, color: src ? '#fff' : '#666' }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>{title}</div>
        <div style={{ fontSize: 11.5, fontStyle: 'italic', opacity: 0.9, marginTop: 2 }}>{subtitle}</div>
      </div>
    </Link>
  );
}

// Plain rectangle image link, no title/caption — used by the service strip.
// Skips rendering its own box entirely if no image is set for that slot yet,
// rather than showing an empty placeholder tile.
function PromoBox({ src, to }) {
  if (!src) return null;
  return (
    <Link to={to} style={{ display: 'block', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
      <img src={IMG(src)} alt="" loading="lazy" style={{ width: '100%', height: '100%', aspectRatio: '7 / 4', objectFit: 'cover', display: 'block' }} />
    </Link>
  );
}

function Collection({ title, gender, products }) {
  return (
    // 1600 (not 1200, like the rest of the Home page) — matches Shop.js's
    // width so these 4 tiles get the same "grows with a wide monitor"
    // treatment as the full shop grid, since that's what's specifically
    // being compared against here.
    <section style={{ maxWidth: 1600, margin: '0 auto', padding: '28px 24px 8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>{title}</h2>
        <Link to={`${STORE_BASE}/shop?gender=${gender}`} style={{ fontSize: 13, color: 'var(--sf-accent)', textDecoration: 'none', fontWeight: 500 }}>
          View all <i className="ti ti-arrow-right" style={{ fontSize: 12 }} />
        </Link>
      </div>
      {/* auto-fit (not auto-fill) — only 4 products are fetched for this
          row, and auto-fill reserves grid tracks for however many WOULD
          fit at the 210px minimum even when there's nothing to put in
          them, leaving that reserved space blank on the right. auto-fit
          collapses those empty tracks instead, so the 4 real cards grow
          via their 1fr share to fill the row instead of leaving a gap. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 16 }}>
        {products.map(p => <ProductCard key={p.model_no} p={p} />)}
        {products.length === 0 && <div style={{ color: '#aaa', fontSize: 13, padding: 20 }}>Loading…</div>}
      </div>
    </section>
  );
}

// Gold moves with the international market daily — a fixed catalogue price
// would be wrong within hours, so every Pure Gold Frames listing points to
// sales for a live quote instead of showing any stored price. Exported so
// the product detail page (Product.js) applies the exact same rule.
export const isGoldPriced = p => p.category === 'Pure Gold Frames';

function PriceRow({ p, scale = 1 }) {
  const portal = usePortal ? usePortal() : null;
  const customer = portal?.customer;
  if (isGoldPriced(p)) {
    return <span style={{ fontSize: 11.5 * scale, color: 'var(--sf-muted)', fontStyle: 'italic' }}>Contact sales — priced at the day's gold rate</span>;
  }
  if (customer && p.price != null) {
    const cur = portal.cart?.currency || 'INR';
    if (p.discount_pct > 0 && p.original_price != null) {
      return (
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, color: 'var(--sf-primary)', fontSize: 15.5 * scale }}>{cur} {p.price}</span>
          <span style={{ textDecoration: 'line-through', color: 'var(--sf-muted)', fontSize: 12 * scale }}>{cur} {p.original_price}</span>
          <span style={{ fontSize: 10.5 * scale, fontWeight: 700, color: '#fff', background: '#dc2626', borderRadius: 4, padding: '1px 5px' }}>
            {p.discount_pct}% OFF
          </span>
        </span>
      );
    }
    return <span style={{ fontWeight: 700, color: 'var(--sf-primary)', fontSize: 15.5 * scale }}>{cur} {p.price}</span>;
  }
  return <span style={{ fontSize: 11.5 * scale, color: 'var(--sf-muted)' }}>{customer ? 'Price on request' : 'Login for price'}</span>;
}

// fontScale: 'sm'|'md'|'lg' independent text-size toggle on top of the
// cols-driven card sizing. Image box is always a square (aspect-ratio 1/1)
// so it fills consistently regardless of how wide the card ends up.
const FONT_SCALE = { sm: 0.87, md: 1, lg: 1.15 };
const qtyBtnStyle = { width: 24, height: 26, border: 'none', background: '#f5f5f5', color: '#333', fontSize: 14, fontWeight: 700, cursor: 'pointer', lineHeight: 1 };

export function ProductCard({ p, cols = 3, fontScale = 'md' }) {
  const scale = FONT_SCALE[fontScale] || 1;
  let portal = null;
  try { portal = usePortal(); } catch (e) { portal = null; }
  const nav = useNavigate();
  // A swatch click swaps which colour is "active" on this card — its own
  // image and variant_id — without navigating away or touching p itself.
  const [active, setActive] = useState({ variant_id: p.variant_id, image: p.image, hover: p.hover_image, color: p.swatches?.[0]?.color });
  // Driven by state rather than imperative DOM src-swapping on mouseenter/
  // mouseleave — that approach went stale the moment a swatch was clicked
  // while the cursor was already resting on the card (the normal way to
  // reach a swatch), since no fresh mouseenter fired to pick up the new
  // colour's hover image. Deriving the shown image from (hovering, active)
  // on every render means a swatch click updates it immediately either way.
  const [hovering, setHovering] = useState(false);
  const inWishlist = portal?.wishlist?.items?.some(it => it.variant_id === active.variant_id);
  const inCompare = portal?.compare?.items?.some(it => it.variant_id === active.variant_id);
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);
  // Only offer a direct add-to-cart on the grid card when this customer can
  // actually see a price for it — otherwise there's nothing to buy against.
  // Gold frames never have a self-serve price, so never a direct add-to-cart.
  const canBuy = !!portal?.customer && p.price != null && !isGoldPriced(p);

  const toggleWishlist = async e => {
    e.preventDefault(); e.stopPropagation();
    if (!portal?.customer) return nav(`${STORE_BASE}/login`);
    try {
      const r = inWishlist ? await PORTAL.delete(`/wishlist/${active.variant_id}`) : await PORTAL.post('/wishlist', { variant_id: active.variant_id });
      portal.setWishlist(r.data);
    } catch (err) { /* ignore — non-critical toggle */ }
  };
  const toggleCompare = async e => {
    e.preventDefault(); e.stopPropagation();
    if (!portal?.customer) return nav(`${STORE_BASE}/login`);
    try {
      const r = inCompare ? await PORTAL.delete(`/compare/${active.variant_id}`) : await PORTAL.post('/compare', { variant_id: active.variant_id });
      portal.setCompare(r.data);
    } catch (err) { alert(err.response?.data?.error || 'Could not update compare list'); }
  };
  const stepQty = (e, delta) => {
    e.preventDefault(); e.stopPropagation();
    setQty(q => Math.max(1, q + delta));
  };
  const addToCart = async e => {
    e.preventDefault(); e.stopPropagation();
    setAdding(true);
    try {
      const r = await PORTAL.post('/cart', { variant_id: active.variant_id, qty });
      portal.setCart(r.data);
    } catch (err) { alert(err.response?.data?.error || 'Could not add to cart'); }
    setAdding(false);
  };
  const pickSwatch = (e, sw) => {
    e.preventDefault(); e.stopPropagation();
    // Falls back to the model's own hover shot if this colour has no banner/
    // angle45 of its own, so hover never goes dead after switching colours.
    setActive({ variant_id: sw.variant_id, image: sw.thumb, hover: sw.hover || p.hover_image, color: sw.color });
  };

  return (
    // minWidth: 0 overrides a grid item's default "never shrink below my
    // content's natural size" floor — without it, a card whose content
    // happens to need a touch more room (a longer swatch row, longer specs
    // text) claims extra width from its `1fr` track while its siblings
    // shrink to fit, so a "3 equal columns" grid silently renders unequal
    // ones. This single fix on the card itself (rather than each caller's
    // grid) covers every grid that uses ProductCard — Shop, Home's
    // collections, and Product's related section alike.
    // height:'100%' + flex column on every layer below is what actually makes
    // this stretch to match its row's tallest sibling — the grid item itself
    // already stretches by CSS Grid default, but <Link> renders an inline <a>
    // that ignores height entirely, so without this the visible white card
    // sits at its own content height and cards with shorter title/spec text
    // look shorter than their row-mates. Not logged-in cards are especially
    // exposed to this because they lack the fixed-height qty/Add-to-Cart row
    // that (by coincidence, not by fix) padded logged-in cards enough to hide it.
    <div style={{ position: 'relative', minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 2, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <button onClick={toggleWishlist} title={inWishlist ? 'Remove from wish list' : 'Add to wish list'}
          style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: '#fff', boxShadow: '0 1px 6px rgba(0,0,0,0.18)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className={`ti ${inWishlist ? 'ti-heart-filled' : 'ti-heart'}`} style={{ fontSize: 14, color: inWishlist ? '#e0245e' : '#555' }} />
        </button>
        <button onClick={toggleCompare} title={inCompare ? 'Remove from compare' : 'Add to compare'}
          style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: '#fff', boxShadow: '0 1px 6px rgba(0,0,0,0.18)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="ti ti-arrows-right-left" style={{ fontSize: 13, color: inCompare ? 'var(--sf-accent)' : '#555' }} />
        </button>
      </div>
      <Link to={`${STORE_BASE}/product/${encodeURIComponent(p.slug || p.model_no)}`} style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flex: 1 }}>
        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, overflow: 'hidden', transition: 'box-shadow .15s', boxShadow: hovering ? '0 6px 20px rgba(0,0,0,0.08)' : 'none', display: 'flex', flexDirection: 'column', flex: 1, width: '100%' }}
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}>
          {/* Front view on load; hovering swaps to the lifestyle banner shot
              (or the 45° angle if no banner is uploaded for this model) —
              reverts to the active colour's front view when the cursor leaves.
              Also picks up a swatch change made while still hovering. */}
          <div style={{ aspectRatio: '1 / 1', background: '#fbfbfb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
            {active.image
              ? <img data-role="hero" loading="lazy" src={IMG(hovering && active.hover ? active.hover : active.image)}
                  alt={[p.shape, p.material].filter(Boolean).join(' ') ? `${[p.shape, p.material].filter(Boolean).join(' ')} eyeglass frame — model ${p.model_no}` : `Eyeglass frame — model ${p.model_no}`}
                  style={{ width: '97%', height: '97%', objectFit: 'contain', transition: 'opacity .2s ease' }}
                  onError={e => { e.target.style.display = 'none'; e.target.parentNode.innerHTML = '<i class="ti ti-eyeglass-2" style="font-size:40px;color:#ddd"></i>'; }} />
              : <i className="ti ti-eyeglass-2" style={{ fontSize: 40, color: '#ddd' }} />}
          </div>
          <div style={{ padding: '12px 13px 13px', display: 'flex', flexDirection: 'column', flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 15 * scale }}>{p.model_no}</div>
            <div style={{ fontSize: 11.5 * scale, color: '#888', margin: '2px 0 8px' }}>
              {[p.shape, p.material].filter(Boolean).join(' · ')}
            </div>
            {/* Pinned to the bottom of the card (marginTop: auto) rather than
                sitting right under the subtitle — that's what keeps the price/
                swatches/cart row level across a row of cards whose title or
                spec text wraps to a different number of lines. */}
            <div style={{ marginTop: 'auto' }}>
              <PriceRow p={p} scale={scale} />
              {/* Always reserves the same height whether or not this model has
                  multiple colours, so cards in the same row line up evenly. */}
              <div className="sf-swatch-row" style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '9px 1px 2px', minHeight: 37, boxSizing: 'border-box' }}>
                {p.swatches?.length > 1 && p.swatches.map(sw => (
                  <button key={sw.variant_id} onClick={e => pickSwatch(e, sw)} title={sw.color_name || sw.color}
                    style={{
                      flex: '0 0 auto', width: 26, height: 26, padding: 0, borderRadius: 6, overflow: 'hidden', cursor: 'pointer',
                      border: active.variant_id === sw.variant_id ? '2px solid var(--sf-accent)' : '1px solid #e2e2e2',
                      background: '#fafafa',
                    }}>
                    {sw.thumb
                      ? <img src={IMG(sw.thumb)} alt={sw.color} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <span style={{ display: 'block', width: '100%', height: '100%' }} />}
                  </button>
                ))}
              </div>
              {canBuy && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #ddd', borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
                    <button onClick={e => stepQty(e, -1)} style={qtyBtnStyle}>−</button>
                    <span style={{ width: 24, textAlign: 'center', fontSize: 12 * scale }}>{qty}</span>
                    <button onClick={e => stepQty(e, 1)} style={qtyBtnStyle}>+</button>
                  </div>
                  <button onClick={addToCart} disabled={adding} className="sf-btn-outline"
                    style={{ flex: 1, border: '1.5px solid var(--sf-accent)', borderRadius: 8, padding: '6.5px 8px', fontSize: 11.5 * scale, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.3, textTransform: 'uppercase' }}>
                    {adding ? 'Adding…' : 'Add to Cart'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}
